import React, { useState, ReactNode } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useClick,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react';
import { TodoItem } from '../types/todo';
import './StatsChart.css';

interface StatsChartProps {
  todos: TodoItem[];
  children: ReactNode;
}

interface DayStats {
  date: string;
  dayName: string;
  completed: number;
  total: number;
}

export const StatsChart: React.FC<StatsChartProps> = ({ todos, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Calculate stats for the last 7 days
  const getWeeklyStats = (): DayStats[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days: DayStats[] = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      const dayTodos = todos.filter(todo => {
        if (!todo.completedAt) return false;
        const completedDate = new Date(todo.completedAt);
        const completedDay = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
        return completedDay.getTime() === date.getTime();
      });
      
      days.push({
        date: date.toISOString().split('T')[0],
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        completed: dayTodos.length,
        total: dayTodos.length,
      });
    }
    
    return days;
  };

  const weeklyStats = getWeeklyStats();
  const maxCompleted = Math.max(...weeklyStats.map(day => day.completed), 1);
  const totalCompleted = weeklyStats.reduce((sum, day) => sum + day.completed, 0);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    middleware: [
      offset(10),
      flip(),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context);
  const focus = useFocus(context);
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ]);

  const getInterestingFact = () => {
    if (totalCompleted === 0) return "Ready to start your productivity journey!";
    if (totalCompleted === 1) return "Great start! One task completed this week.";
    if (totalCompleted < 5) return `${totalCompleted} tasks completed this week. Building momentum!`;
    if (totalCompleted < 10) return `Strong week with ${totalCompleted} completed tasks!`;
    if (totalCompleted < 20) return `Excellent productivity: ${totalCompleted} tasks completed!`;
    return `Outstanding! ${totalCompleted} tasks completed this week. You're on fire! 🔥`;
  };

  return (
    <>
      <div 
        className="stats-chart-container"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {children}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            className="stats-tooltip"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <div className="tooltip-header">
              <h4>This Week's Progress</h4>
              <span className="tooltip-total">{totalCompleted} completed</span>
            </div>
            
            <div className="tooltip-daily-breakdown">
              {weeklyStats.map((day, index) => {
                const isToday = index === weeklyStats.length - 1;
                const displayName = isToday ? 'TODAY' : day.dayName;
                
                return (
                  <div key={day.date} className={`tooltip-day ${isToday ? 'today' : ''}`}>
                    <span className="tooltip-day-name">{displayName}</span>
                    <div className="tooltip-day-bar">
                      <div 
                        className="tooltip-day-fill"
                        style={{
                          width: day.completed === 0 ? '2px' : `${(day.completed / maxCompleted) * 100}%`,
                          background: day.completed === 0 ? '#e2e8f0' : (isToday ? '#10b981' : '#3b82f6')
                        }}
                      />
                    </div>
                    <span className="tooltip-day-count">{day.completed}</span>
                  </div>
                );
              })}
            </div>
            
            <div className="tooltip-fact">
              {getInterestingFact()}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
